import type { AccountBalance, Currency, LedgerAccount, LinkedBorrowingDebt, Money } from "../../entities/ledger/types";

// One reading of an account's money, so every surface tells the same story.
// It is read from the server's position, which has already resolved whether
// the account holds or owes; nothing here looks at a sign or an account type.
export interface AccountReading {
  // What the account holds. Never negative: a debt is not a negative balance.
  balance: Money;
  // What the account owes by its own history, present only when it owes.
  debt: Money | null;
  // True when the account owes and no Borrowing answers for it yet.
  unattributed: boolean;
  // The Borrowing debts this account is the facility of. They are the credit
  // obligation; the account's own debt above is its endpoint position.
  borrowings: LinkedBorrowingDebt[];
  // What the provider last reported as spendable, unused credit included. An
  // observation shown beside the balance, never in its place.
  available: { amount: Money; observedAt?: string } | null;
  // The book-currency value of whichever side the account is on, for an
  // account kept in another currency.
  book: { value: Money; owed: boolean } | null;
  // True when the account could not be valued in the book currency at all.
  bookUnavailable: boolean;
}

export function isSpendable(account: LedgerAccount): boolean {
	void account;
	return true;
}

export function readAccount(account: LedgerAccount, balance?: AccountBalance): AccountReading {
  const zero = { minor: 0, currency: account.currency as Currency };
  const position = balance?.position;
  const owed = position?.native.owed;
  const availableFunds = balance?.state?.availableFunds;
  let book: AccountReading["book"] = null;
  if (position?.book && position.book.held.currency !== account.currency) {
    book = position.book.owed.minor > 0
      ? { value: position.book.owed, owed: true }
      : { value: position.book.held, owed: false };
  }
  return {
    balance: position?.native.held ?? zero,
    debt: owed && owed.minor > 0 ? owed : null,
    unattributed: balance?.debtAttribution === "unattributed",
    borrowings: balance?.linkedBorrowings ?? [],
    available: availableFunds ? { amount: availableFunds, observedAt: balance?.state?.availableFundsAt } : null,
    book,
    bookUnavailable: Boolean(position) && !position?.book,
  };
}

export function observedAt(value: string): string {
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    .format(new Date(value));
}

export function debtSourceLabel(source?: string): string {
  switch (source) {
    case "reported": return "reported by the bank";
    case "rolled_forward": return "bank figure carried forward by later account activity";
    case "derived": return "derived from the facility";
    case "replay": return "from recorded borrowing events";
    default: return "unknown";
  }
}
